import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { searchPartsCatalog } from "../data/partsSearch";

const DEFAULT_PAGE_SIZE = 12;

const SORT_OPTIONS = [
  { value: "RELEVANCE", label: "Relevanz" },
  { value: "PRICE", label: "Preis" },
  { value: "AVAILABILITY", label: "Verfügbarkeit" },
];

const FITMENT_CONFIG = {
  SAFE: { label: "passt sicher", color: "success", variant: "filled" },
  UNSURE: { label: "unsicher", color: "warning", variant: "filled" },
  UNKNOWN: { label: "unbekannt", color: "default", variant: "outlined" },
};

const AVAILABILITY_CONFIG = {
  IN_STOCK: { label: "Sofort verfügbar", color: "success" },
  LOW_STOCK: { label: "Begrenzt verfügbar", color: "warning" },
  OUT_OF_STOCK: { label: "Zurzeit nicht verfügbar", color: "default" },
  UNKNOWN: { label: "Verfügbarkeit unbekannt", color: "default" },
};

const DEFAULT_IMAGE = "https://via.placeholder.com/320x200.png?text=Teil";

function formatCurrency(value, currency = "EUR") {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return null;
  }
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(value);
}

function formatLeadTime(availability) {
  const leadTime = availability?.leadTimeDays;
  if (typeof leadTime !== "number" || leadTime < 0) {
    return null;
  }
  if (leadTime === 0) {
    return "Lieferung am gleichen Tag";
  }
  if (leadTime === 1) {
    return "Lieferzeit ca. 1 Tag";
  }
  return `Lieferzeit ca. ${leadTime} Tage`;
}

function FitmentChip({ score }) {
  const config = FITMENT_CONFIG[score] ?? FITMENT_CONFIG.UNKNOWN;
  return (
    <Chip
      size="small"
      color={config.color}
      variant={config.variant}
      label={config.label}
    />
  );
}

function AvailabilityChip({ availability }) {
  const status = availability?.status || "UNKNOWN";
  const config = AVAILABILITY_CONFIG[status] ?? AVAILABILITY_CONFIG.UNKNOWN;
  const quantityLabel =
    typeof availability?.quantity === "number" && availability.quantity > 0
      ? ` · ${availability.quantity} Stk`
      : "";

  return (
    <Chip
      size="small"
      color={config.color === "default" ? "default" : config.color}
      variant={config.color === "default" ? "outlined" : "filled"}
      label={`${config.label}${quantityLabel}`}
    />
  );
}

function buildFacetOptions(facets = [], selected = []) {
  const map = new Map();
  facets.forEach((facet) => {
    if (!facet || !facet.value) {
      return;
    }
    map.set(facet.value, {
      value: facet.value,
      label: facet.label || facet.value,
      count: facet.count ?? 0,
    });
  });

  selected.forEach((value) => {
    if (!map.has(value)) {
      map.set(value, { value, label: value, count: 0 });
    }
  });

  return Array.from(map.values()).sort((a, b) =>
    a.label.localeCompare(b.label, "de-DE", { sensitivity: "base" })
  );
}

function toggleFacetValue(values, nextValue) {
  if (values.includes(nextValue)) {
    return values.filter((value) => value !== nextValue);
  }
  return [...values, nextValue];
}

export default function PartsCatalogView() {
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [safeFitmentOnly, setSafeFitmentOnly] = useState(false);
  const [sort, setSort] = useState("RELEVANCE");
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(searchText.trim());
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchText]);

  const normalizedCategories = useMemo(
    () => [...new Set(selectedCategories)].sort((a, b) => a.localeCompare(b, "de-DE", { sensitivity: "base" })),
    [selectedCategories]
  );

  const normalizedBrands = useMemo(
    () => [...new Set(selectedBrands)].sort((a, b) => a.localeCompare(b, "de-DE", { sensitivity: "base" })),
    [selectedBrands]
  );

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["parts-search"] });
  }, [queryClient, normalizedCategories, normalizedBrands, safeFitmentOnly]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, normalizedCategories, normalizedBrands, sort, safeFitmentOnly]);

  const queryParams = useMemo(
    () => ({
      search: debouncedSearch,
      categories: normalizedCategories,
      brands: normalizedBrands,
      sort,
      page,
      pageSize: DEFAULT_PAGE_SIZE,
      fitmentOnly: safeFitmentOnly,
    }),
    [
      debouncedSearch,
      normalizedCategories,
      normalizedBrands,
      sort,
      page,
      safeFitmentOnly,
    ]
  );

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["parts-search", queryParams],
    queryFn: () => searchPartsCatalog(queryParams),
    keepPreviousData: true,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (data?.page && data.page !== page) {
      setPage(data.page);
    }
  }, [data?.page, page]);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageSize = data?.pageSize ?? DEFAULT_PAGE_SIZE;
  const totalPages = total > 0 ? Math.ceil(total / pageSize) : 0;
  const rawCategoryFacets = data?.facets?.categories ?? [];
  const rawBrandFacets = data?.facets?.brands ?? [];

  const categoryFacetOptions = useMemo(
    () => buildFacetOptions(rawCategoryFacets, normalizedCategories),
    [rawCategoryFacets, normalizedCategories]
  );
  const brandFacetOptions = useMemo(
    () => buildFacetOptions(rawBrandFacets, normalizedBrands),
    [rawBrandFacets, normalizedBrands]
  );

  const hasActiveFilters =
    normalizedCategories.length > 0 ||
    normalizedBrands.length > 0 ||
    safeFitmentOnly;

  const startIndex = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIndex = total === 0 ? 0 : Math.min(total, page * pageSize);
  const resultSummary = total === 0
    ? "Keine Teile gefunden"
    : `Zeige ${startIndex}–${endIndex} von ${total} Teilen`;

  const handleCategoryChange = (value) => {
    setSelectedCategories((prev) => toggleFacetValue(prev, value));
  };

  const handleBrandChange = (value) => {
    setSelectedBrands((prev) => toggleFacetValue(prev, value));
  };

  const handleClearFilters = () => {
    setSelectedCategories([]);
    setSelectedBrands([]);
    setSafeFitmentOnly(false);
  };

  return (
    <Paper sx={{ p: { xs: 2, md: 3 } }}>
      <Stack spacing={3}>
        <Typography variant="h6">Teilesuche & Ergebnisse</Typography>
        <Divider />
        <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">
          <Box sx={{ width: { xs: "100%", md: 280 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1">Filter</Typography>
              {hasActiveFilters && (
                <Button size="small" onClick={handleClearFilters}>
                  Zurücksetzen
                </Button>
              )}
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Kategorie
                </Typography>
                <FormGroup>
                  {categoryFacetOptions.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Keine Kategorien für die aktuelle Suche.
                    </Typography>
                  )}
                  {categoryFacetOptions.map((facet) => (
                    <FormControlLabel
                      key={facet.value}
                      control={
                        <Checkbox
                          size="small"
                          checked={normalizedCategories.includes(facet.value)}
                          onChange={() => handleCategoryChange(facet.value)}
                        />
                      }
                      label={`${facet.label} (${facet.count})`}
                    />
                  ))}
                </FormGroup>
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Marke
                </Typography>
                <FormGroup>
                  {brandFacetOptions.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Keine Marken für die aktuelle Suche.
                    </Typography>
                  )}
                  {brandFacetOptions.map((facet) => (
                    <FormControlLabel
                      key={facet.value}
                      control={
                        <Checkbox
                          size="small"
                          checked={normalizedBrands.includes(facet.value)}
                          onChange={() => handleBrandChange(facet.value)}
                        />
                      }
                      label={`${facet.label} (${facet.count})`}
                    />
                  ))}
                </FormGroup>
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Passgenauigkeit
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={safeFitmentOnly}
                        onChange={(event) => setSafeFitmentOnly(event.target.checked)}
                      />
                    }
                    label="Nur sichere Passung"
                  />
                </FormGroup>
              </Box>
            </Stack>
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                <TextField
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  placeholder="Artikel, Nummer oder OE-Referenz"
                  label="Suche"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 200 } }}>
                  <InputLabel id="parts-sort-label">Sortierung</InputLabel>
                  <Select
                    labelId="parts-sort-label"
                    label="Sortierung"
                    value={sort}
                    onChange={(event) => setSort(event.target.value)}
                  >
                    {SORT_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {isLoading ? "Lade Teile..." : resultSummary}
              </Typography>
              <Box sx={{ position: "relative" }}>
                {isFetching && !isLoading && (
                  <LinearProgress
                    sx={{
                      position: "absolute",
                      top: -6,
                      left: 0,
                      right: 0,
                      borderRadius: 999,
                    }}
                  />
                )}
                {isLoading ? (
                  <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
                    <CircularProgress />
                  </Box>
                ) : error ? (
                  <Box sx={{ py: 6 }}>
                    <Typography color="error">
                      {error.message || "Fehler beim Laden der Teile."}
                    </Typography>
                  </Box>
                ) : items.length === 0 ? (
                  <Box sx={{ py: 6 }}>
                    <Typography variant="body1">Keine Teile gefunden.</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Passen Sie die Suche oder die Filter an, um mehr Ergebnisse zu erhalten.
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={2}>
                    <Grid container spacing={2}>
                      {items.map((part) => {
                        const oeReferences = Array.isArray(part.oeReferences)
                          ? part.oeReferences
                          : [];
                        const visibleRefs = oeReferences.slice(0, 4);
                        const hiddenCount = Math.max(oeReferences.length - visibleRefs.length, 0);
                        const formattedPrice = formatCurrency(part.bestOfferPrice, part.currency);
                        const leadTimeText = formatLeadTime(part.availability);

                        return (
                          <Grid item xs={12} sm={6} lg={4} key={part.id}>
                            <Card
                              variant="outlined"
                              sx={{
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              <CardMedia
                                component="img"
                                height="160"
                                image={part.imageUrl || DEFAULT_IMAGE}
                                alt={part.name}
                                sx={{ objectFit: "cover" }}
                              />
                              <CardContent sx={{ flexGrow: 1 }}>
                                <Stack spacing={1.5}>
                                  <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                                    <Box>
                                      <Typography variant="subtitle1">{part.name}</Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        {[part.brand, part.partNumber].filter(Boolean).join(" · ")}
                                      </Typography>
                                    </Box>
                                    <FitmentChip score={part.fitmentScore} />
                                  </Stack>
                                  {visibleRefs.length > 0 && (
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                      {visibleRefs.map((ref) => (
                                        <Chip key={ref} size="small" variant="outlined" label={ref} />
                                      ))}
                                      {hiddenCount > 0 && (
                                        <Chip size="small" variant="outlined" label={`+${hiddenCount}`} />
                                      )}
                                    </Stack>
                                  )}
                                  {formattedPrice && (
                                    <Typography variant="body2">
                                      {`ab ${formattedPrice}`}
                                      {typeof part.offersCount === "number" && part.offersCount > 0
                                        ? ` · ${part.offersCount} Angebot${part.offersCount === 1 ? "" : "e"}`
                                        : ""}
                                    </Typography>
                                  )}
                                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <AvailabilityChip availability={part.availability} />
                                    {leadTimeText && (
                                      <Typography variant="caption" color="text.secondary">
                                        {leadTimeText}
                                      </Typography>
                                    )}
                                  </Stack>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                    {totalPages > 1 && (
                      <Stack alignItems="center" sx={{ pt: 1 }}>
                        <Pagination
                          count={totalPages}
                          page={page}
                          onChange={(_, value) => setPage(value)}
                          color="primary"
                        />
                      </Stack>
                    )}
                  </Stack>
                )}
              </Box>
            </Stack>
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
}

